import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { bindErrorsToNgControl, bindTouchedToNgControl, UiFormFieldControl } from '@/ui/form-field/ui-form-field-control.class';
import { UiIconComponent } from '@/ui/icon/ui-icon';
import { frenchSort } from '@/utils/toolkit/array/sort.utils';
import { isNotNullish, isNullish } from '@/utils/typing/is-nullish.predicate';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy,
    Component,
    ContentChild,
    forwardRef,
    inject,
    input,
    Input,
    model,
    type OnDestroy,
    type OnInit,
    type SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { type ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocomplete, type MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatOptgroup, MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { DragulaModule, DragulaService } from 'ng2-dragula';
import { BehaviorSubject, combineLatest, type Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import * as _ from 'underscore';
import { SearchProvider } from '../../providers/search/search.provider';

type ChipOption = any;

interface ChipOptionsGroup {
    label: string;
    options: ChipOption[];
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./ui-selectable-chips.scss'],
    selector: 'ui-selectable-chips',
    templateUrl: 'ui-selectable-chips.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UiSelectableChipsComponent),
            multi: true
        },
        {
            provide: UiFormFieldControl,
            useExisting: forwardRef(() => UiSelectableChipsComponent)
        }
    ],
    imports: [
        MatChipGrid,
        DragulaModule,
        MatChipRow,
        NgTemplateOutlet,
        UiIconComponent,
        MatChipRemove,
        MatInput,
        FormsModule,
        MatAutocompleteTrigger,
        MatChipInput,
        MatAutocomplete,
        MatOptgroup,
        MatOption,
        NgClass,
        AsyncPipe
    ]
})
export class UiSelectableChipsComponent implements ControlValueAccessor, UiFormFieldControl, OnInit, OnDestroy {
    private _dragulaService = inject(DragulaService);
    private _translateService = inject(TranslateService);

    disabled = model(false);
    required = input(false);
    @Input() isDeletable: boolean = false;
    clearable = input(false);
    touched = bindTouchedToNgControl();
    errors = bindErrorsToNgControl();

    private _observableInputsManager = new ObservableInputsManager<UiSelectableChipsComponent>(this);
    @Input() options!: ChipOption[];
    options$ = this._observableInputsManager.observe('options');

    @Input() labelField: string | null = null;
    @Input() groupField: string = '';

    visible = true;
    selectable = true;
    addOnBlur = true;
    separatorKeysCodes: number[] = [ENTER, COMMA];

    search$: BehaviorSubject<string> = new BehaviorSubject('');
    selectedOptions$ = new BehaviorSubject<ChipOption[]>([]);
    filteredOptions$!: Observable<ChipOption[]>;
    optionsGroup$: ReplaySubject<ChipOptionsGroup[]> = new ReplaySubject(1);
    filteredOptionsGroup$!: Observable<ChipOptionsGroup[]>;

    @ViewChild(MatInput, { static: true }) matInput!: MatInput;
    @ViewChild('auto') matAutocomplete!: MatAutocomplete;
    @ContentChild(TemplateRef) optionTemplateRef?: TemplateRef<any>;

    dragulaGroup = 'chip-list';

    dynamicHeight() {
        return true;
    }

    ngOnInit() {
        this.options$.pipe(filter(isNotNullish), distinctUntilChanged(), untilDestroyed(this)).subscribe(options => {
            if (this.groupField) {
                const translatedOptions: ChipOption[] = this.sortOptions(options).map(option => {
                    if (isNullish(option[this.groupField])) {
                        option[this.groupField] = this._translateService.instant('STUFF.OTHER');
                    }
                    return option;
                });

                const groups: ChipOptionsGroup[] = Object.entries(
                    _.groupBy(translatedOptions, (option: any) => option[this.groupField])
                ).reduce((acc, [key, value]) => {
                    acc.push({ label: key, options: value as ChipOption[] });
                    return acc;
                }, [] as ChipOptionsGroup[]);

                this.optionsGroup$.next(groups);
            }
        });

        this.filteredOptions$ = this.search$.pipe(
            startWith(null as unknown as string),
            filter(search => typeof search === 'string' || isNullish(search)),
            map((search: string) =>
                search
                    ? this.filterOptionsWithInputSearch(search, this.sortOptions(this.options))
                    : this.options.slice().filter(option => !this.selectedOptions$.getValue().includes(option))
            )
        );

        this.filteredOptionsGroup$ = combineLatest([this.search$, this.optionsGroup$]).pipe(
            filter(([search, _]) => typeof search === 'string' || isNullish(search)),
            map(([search, optionGroup]) => {
                return [
                    ...optionGroup.map(group => {
                        return {
                            ...group,
                            options: [
                                ...(search
                                    ? this.filterOptionsWithInputSearch(search, group.options)
                                    : group.options.slice().filter(option => !this.selectedOptions$.getValue().includes(option)))
                            ]
                        };
                    })
                ].filter(group => group.options.length);
            })
        );

        this._dragulaService.createGroup(this.dragulaGroup, {
            isContainer(el) {
                if (!el) return false;
                return el.classList.contains('mat-chip-list-wrapper');
            },
            direction: 'horizontal'
        });

        this._dragulaService
            .dropModel(this.dragulaGroup)
            .pipe(untilDestroyed(this))
            .subscribe(args => {
                this.updateSelectedOptions(args.sourceModel);
            });
    }

    ngOnDestroy() {
        this._dragulaService.destroy(this.dragulaGroup);
    }

    onTouched: () => void = () => {};
    onChange: (value: any) => void = () => {};

    writeValue(value: ChipOption[]): void {
        this.selectedOptions$.next(value ? value : []);
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled.set(disabled);
    }

    shouldLabelFloat() {
        return this.matInput.shouldLabelFloat || (this.selectedOptions$.getValue() && this.selectedOptions$.getValue().length)
            ? this.matInput.shouldLabelFloat
                ? this.matInput.shouldLabelFloat
                : this.selectedOptions$.getValue().length > 0
            : false;
    }

    focused() {
        return this.matInput.focused;
    }

    get stateChanges() {
        return this.matInput.stateChanges;
    }

    onContainerClick() {
        this.matInput.onContainerClick();
    }

    onClearRequest() {
        this.selectedOptions$.next([]);
    }

    ngOnChanges(changes: SimpleChanges) {
        this._observableInputsManager.triggerChanges(changes);
    }

    remove(index: number): void {
        const list = this.selectedOptions$.getValue();
        if (index >= 0) {
            list.splice(index, 1);
            this.updateSelectedOptions(list);
        }
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        const selectedOptions = this.selectedOptions$.getValue();
        selectedOptions.push(event.option.value);

        this.updateSelectedOptions(selectedOptions);
        this.search$.next('');
        this.matInput.value = '';
    }

    handleSearchEvent(e: any) {
        if (typeof e === 'string') {
            this.search$.next(e);
        }
    }

    private updateSelectedOptions(newSelectedOptions: ChipOption[]) {
        this.selectedOptions$.next(newSelectedOptions);
        this.onChange(newSelectedOptions);
    }

    private filterOptionsWithInputSearch(inputSearch: string, options: ChipOption[]): ChipOption[] {
        const searchText = inputSearch.toLowerCase();
        let filteredOptions: ChipOption[] = [];
        if (this.labelField) {
            const labelField = this.labelField;
            filteredOptions = options.filter(option => SearchProvider.matchAllWordsStatic([option[labelField]], searchText.split(' ')));
        } else {
            filteredOptions = options.filter(option => SearchProvider.matchAllWordsStatic([option], searchText.split(' ')));
        }

        return filteredOptions.filter(option => !this.selectedOptions$.getValue().includes(option));
    }

    private sortOptions(options: ChipOption[]) {
        let sortedOptions: ChipOption[] = [...options];
        if (this.labelField) {
            const labelField = this.labelField;
            sortedOptions = sortedOptions.sort((a, b) => frenchSort(a[labelField], b[labelField]));
        } else {
            sortedOptions = sortedOptions.sort((a, b) => frenchSort(a, b));
        }
        return this.groupField ? sortedOptions.sort((a, b) => frenchSort(a[this.groupField], b[this.groupField])) : sortedOptions;
    }
}
